import type { ContentTypeDefinition } from '../../../lib/flotiq-client/types/api-response';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';

import { CaretDownIcon } from '../../../images/shapes';

import FieldRuleCheckbox from './FieldRuleCheckbox/FieldRuleCheckbox';

export type FieldPermissionsRule = {
  id: string;
  name: string;
  label?: string;
  isFieldPermission?: boolean;
};

interface FieldPermissionsProps {
  /**
   * Permission available rules
   */
  ruleColumn?: FieldPermissionsRule[];
  /**
   * Content type options
   */
  contentTypeDefinition?: {
    metaDefinition?: ContentTypeDefinition['metaDefinition'];
    required?: string[];
  };
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Component test ID
   */
  testId?: string;
}

const FieldPermissions = ({
  ruleColumn = EMPTY_ARRAY,
  contentTypeDefinition,
  disabled = false,
  testId,
}: FieldPermissionsProps) => {
  const { t } = useTranslation();

  if (!contentTypeDefinition?.metaDefinition) return;

  return (
    <Disclosure defaultOpen={false}>
      {({ open }) => (
        <div className="rounded-lg bg-blue-50 dark:bg-slate-800/70 relative w-full text-sm dark:text-white col-span-2">
          <DisclosureButton
            className="flex w-full items-center justify-between cursor-pointer uppercase
                      p-4 border-b border-white dark:border-slate-950 dark:text-slate-400"
            {...getTestProps(testId, 'panel-open')}
          >
            {t('UserRoles.FieldsPermissions')}
            <CaretDownIcon
              className={twMerge(
                'w-3 transition-transform duration-300 ease-in-out',
                open && 'rotate-180',
              )}
            />
          </DisclosureButton>

          <DisclosurePanel
            className="py-1.5 grid grid-cols-1 sm:grid-cols-2 gap-y-2 items-center
                        duration-100 ease-out data-closed:scale-95 data-closed:opacity-0"
            transition
          >
            {(contentTypeDefinition.metaDefinition?.order || []).map(
              (propertyKey) => (
                <Fragment key={`${propertyKey}-field-rules`}>
                  <span className="px-4">
                    {contentTypeDefinition.metaDefinition?.propertiesConfig?.[
                      propertyKey
                    ]?.label || propertyKey}

                    {contentTypeDefinition.required?.includes(propertyKey) && (
                      <RequiredTemplate />
                    )}
                  </span>

                  <div className="grid grid-cols-4 py-3">
                    <div className="col-span-3 grid grid-cols-3 justify-items-center">
                      {ruleColumn.map(({ name, isFieldPermission }) => {
                        if (!isFieldPermission) return null;
                        return (
                          <FieldRuleCheckbox
                            key={`${propertyKey}-${name}`}
                            ruleName={name}
                            propertyKey={propertyKey}
                            disabled={disabled}
                            {...getTestProps(
                              testId,
                              `${propertyKey}-${name}`,
                              'testId',
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                </Fragment>
              ),
            )}
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
};

export default FieldPermissions;
